import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useCompanies, useRoles, useContacts, useStats } from '../hooks/useFirestore';
import { exportToExcel } from '../utils/excelExport';
import { Icons } from './Icons';
import CompanyCard from './CompanyCard';
import StatsBar from './StatsBar';
import QuickPads from './QuickPads';
import SettingsPanel from './SettingsPanel';
import TemplatesPanel from './TemplatesPanel';
import StandaloneContacts from './StandaloneContacts';
import MultiSelectFilter from './MultiSelectFilter';
import { locationMatchesQuery } from '../utils/usStates';
import './MultiSelectFilter.css';
import './Dashboard.css';

const PAGE_SIZE = 20;
const STATUS_LIST = ['Not Applied', 'Applied', 'Interviewing', 'Offer', 'Rejected'];
const CONTACT_STATUS_LIST = ['Connected', 'Pending'];
const BULK_DELETE_TIME = 6;

export default function Dashboard() {
  const { currentUser, userProfile, logout } = useAuth();
  const toast = useToast();
  const uid = currentUser?.uid;
  const { companies, loading: loadingC, addCompany, updateCompany, deleteCompany } = useCompanies(uid);
  const { roles, loading: loadingR, addRole, updateRole, deleteRole } = useRoles(uid);
  const { contacts, loading: loadingCt, addContact, updateContact, deleteContact } = useContacts(uid);
  const stats = useStats(companies, roles, contacts);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilters, setStatusFilters] = useState([]);
  const [contactFilters, setContactFilters] = useState([]);
  const [stateFilters, setStateFilters] = useState([]);
  const [cityFilters, setCityFilters] = useState([]);
  const [flagFilters, setFlagFilters] = useState([]);
  const [dateFilter, setDateFilter] = useState('');
  const [updateMsgFilter, setUpdateMsgFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [page, setPage] = useState(1);
  const [activePanel, setActivePanel] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [readability, setReadability] = useState(() => localStorage.getItem('tt-readability') || 'standard');
  const [viewLayout, setViewLayout] = useState(() => localStorage.getItem('tt-layout') || 'list');
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkPendingDelete, setBulkPendingDelete] = useState(null);
  const [bulkCountdown, setBulkCountdown] = useState(BULK_DELETE_TIME);
  const bulkDeleteTimer = useRef(null);
  const bulkIntervalRef = useRef(null);
  const menuRef = useRef(null);

  const showDates = userProfile?.showDates !== false;
  const locationMode = userProfile?.locationMode || 'usStates';

  useEffect(() => { document.title = 'Dashboard | TrackThrough'; }, []);
  useEffect(() => { document.body.setAttribute('data-readability', readability); localStorage.setItem('tt-readability', readability); }, [readability]);
  useEffect(() => { localStorage.setItem('tt-layout', viewLayout); }, [viewLayout]);
  useEffect(() => {
    function handler(e) { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  useEffect(() => { return () => { clearTimeout(bulkDeleteTimer.current); clearInterval(bulkIntervalRef.current); }; }, []);

  // Helper: parse location into state and city
  function parseLocation(loc) {
    if (!loc) return { state: '', city: '' };
    if (loc === 'Remote') return { state: 'Remote', city: '' };
    const parts = loc.split('·').map(p => p.trim());
    return { state: parts[0] || '', city: parts[1] || '' };
  }

  // Compute unique states and cities from all roles
  const { uniqueStates, uniqueCities } = useMemo(() => {
    const states = new Set();
    const cities = new Set();
    roles.forEach(r => {
      const { state, city } = parseLocation(r.location);
      if (state) states.add(state);
      if (city) cities.add(city);
    });
    return { uniqueStates: [...states].sort(), uniqueCities: [...cities].sort() };
  }, [roles]);

  const hasActiveFilters = statusFilters.length > 0 || contactFilters.length > 0 || stateFilters.length > 0 || cityFilters.length > 0 || dateFilter || flagFilters.length > 0 || updateMsgFilter;

  // Role-level filtering
  const { filteredCompanies, filteredRoles } = useMemo(() => {
    let visibleRoles = [...roles];
    let visibleCompanies = [...companies];

    if (bulkPendingDelete) visibleCompanies = visibleCompanies.filter(c => !bulkPendingDelete.has(c.id));

    if (statusFilters.length > 0) visibleRoles = visibleRoles.filter(r => statusFilters.includes(r.status));
    if (stateFilters.length > 0) {
      visibleRoles = visibleRoles.filter(r => {
        const { state } = parseLocation(r.location);
        return stateFilters.includes(state);
      });
    }
    if (cityFilters.length > 0) {
      visibleRoles = visibleRoles.filter(r => {
        const { city } = parseLocation(r.location);
        return cityFilters.includes(city);
      });
    }
    if (contactFilters.length > 0) {
      const matchingRoleIds = new Set(contacts.filter(c => contactFilters.includes(c.linkedinStatus)).map(c => c.roleId));
      visibleRoles = visibleRoles.filter(r => matchingRoleIds.has(r.id));
    }
    if (flagFilters.length > 0) {
      visibleCompanies = visibleCompanies.filter(c => flagFilters.includes(c.flag || ''));
    }
    if (updateMsgFilter) {
      const companyIdsWithUpdateMsg = new Set(visibleRoles.filter(r => r.updateMessage && r.updateMessage.trim()).map(r => r.companyId));
      visibleRoles = visibleRoles.filter(r => r.updateMessage && r.updateMessage.trim());
      visibleCompanies = visibleCompanies.filter(c => companyIdsWithUpdateMsg.has(c.id));
    }
    if (hasActiveFilters) {
      const companyIdsWithRoles = new Set(visibleRoles.map(r => r.companyId));
      visibleCompanies = visibleCompanies.filter(c => companyIdsWithRoles.has(c.id));
    }
    if (dateFilter) {
      visibleRoles = visibleRoles.filter(r => r.appliedDate && r.appliedDate >= dateFilter);
      const companyIdsWithRoles = new Set(visibleRoles.map(r => r.companyId));
      visibleCompanies = visibleCompanies.filter(c => companyIdsWithRoles.has(c.id));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchingRoleCompanyIds = visibleRoles.filter(r => r.title.toLowerCase().includes(q)).map(r => r.companyId);
      const matchingContactCompanyIds = contacts.filter(c => c.name.toLowerCase().includes(q) || (c.designation || '').toLowerCase().includes(q)).map(c => c.companyId);
      const matchingLocationCompanyIds = visibleRoles.filter(r => locationMatchesQuery(r.location, q)).map(r => r.companyId);
      visibleCompanies = visibleCompanies.filter(c => {
        const name = c.name.toLowerCase();
        if (name.includes(q)) return true;
        if (q.length >= 2 && /^[a-z]+$/.test(q)) {
          const nameWords = name.split(/\s+/).filter(w => w.length > 0);
          if (nameWords.length >= 2) {
            const abbreviation = nameWords.map(w => w[0]).join('');
            if (abbreviation === q || abbreviation.startsWith(q)) return true;
          }
        }
        if (matchingRoleCompanyIds.includes(c.id)) return true;
        if (matchingContactCompanyIds.includes(c.id)) return true;
        if (matchingLocationCompanyIds.includes(c.id)) return true;
        return false;
      });
    }

    return { filteredCompanies: visibleCompanies, filteredRoles: visibleRoles };
  }, [companies, roles, contacts, searchQuery, statusFilters, contactFilters, stateFilters, cityFilters, dateFilter, bulkPendingDelete, hasActiveFilters, flagFilters, updateMsgFilter]);

  const paginatedCompanies = useMemo(() => filteredCompanies.slice(0, page * PAGE_SIZE), [filteredCompanies, page]);
  const hasMore = paginatedCompanies.length < filteredCompanies.length;

  // Compute user's existing cities for typeahead suggestions
  const existingCities = useMemo(() => {
    const cities = new Set();
    roles.forEach(r => {
      const loc = r.location || '';
      if (loc && loc !== 'Remote') {
        const parts = loc.split('·').map(p => p.trim());
        if (parts[1]) cities.add(parts[1]);
      }
    });
    return [...cities].sort();
  }, [roles]);

  async function handleAddCompany(e) {
    e.preventDefault(); if (!newCompanyName.trim()) return;
    try { await addCompany(newCompanyName); setNewCompanyName(''); toast('Company added'); } catch (err) { toast(err.message, 'error'); }
  }

  function handleExport() {
    exportToExcel(filteredCompanies, filteredRoles, contacts, userProfile?.firstName || 'Export', {
      showDates
    });
    toast('Excel exported');
  }

  function handleCopyShareLink() {
    const slug = userProfile?.shareSlug || userProfile?.username;
    navigator.clipboard.writeText(window.location.origin + '/' + slug);
    toast('Share link copied!', 'system');
  }

  function toggleBulkSelect(companyId) {
    setBulkSelected(prev => { const next = new Set(prev); if (next.has(companyId)) next.delete(companyId); else next.add(companyId); return next; });
  }
  function selectAllFiltered() { setBulkSelected(new Set(filteredCompanies.map(c => c.id))); }

  function handleBulkDelete() {
    if (bulkSelected.size === 0) return;
    const idsToDelete = new Set(bulkSelected);
    setBulkPendingDelete(idsToDelete);
    setBulkSelected(new Set());
    setBulkCountdown(BULK_DELETE_TIME);

    clearInterval(bulkIntervalRef.current);
    bulkIntervalRef.current = setInterval(() => setBulkCountdown(c => { if (c <= 1) { clearInterval(bulkIntervalRef.current); return 0; } return c - 1; }), 1000);

    clearTimeout(bulkDeleteTimer.current);
    bulkDeleteTimer.current = setTimeout(async () => {
      clearInterval(bulkIntervalRef.current);
      for (const id of idsToDelete) { await deleteCompany(id); }
      setBulkPendingDelete(null); setBulkMode(false);
    }, BULK_DELETE_TIME * 1000);
  }

  function handleBulkUndo() {
    clearTimeout(bulkDeleteTimer.current); clearInterval(bulkIntervalRef.current);
    bulkDeleteTimer.current = null; bulkIntervalRef.current = null;
    setBulkPendingDelete(null);
    toast('Bulk delete cancelled');
  }

  const loading = loadingC || loadingR || loadingCt;
  if (loading) return <div className="dashboard-loading"><div className="loading-spinner-lg" /><p>Loading your data...</p></div>;

  return (
    <div className="dashboard">
      <header className="dash-header">
        <div className="dash-header-inner">
          <div className="dash-header-left">
            <div className="logo-mark-sm">T T</div>
            <h1 className="dash-title">TrackThrough</h1>
          </div>
          <div className="dash-header-right">
            <div className="readability-toggle">
              <button className={`readability-btn ${readability === 'standard' ? 'active' : ''}`} onClick={() => setReadability('standard')} title="Standard">Aa</button>
              <button className={`readability-btn enhanced ${readability === 'enhanced' ? 'active' : ''}`} onClick={() => setReadability('enhanced')} title="Enhanced">Aa</button>
            </div>
            <div className="view-layout-toggle">
              <button className={`vl-btn ${viewLayout === 'list' ? 'active' : ''}`} onClick={() => setViewLayout('list')} title="List">☰</button>
              <button className={`vl-btn ${viewLayout === 'grid' ? 'active' : ''}`} onClick={() => setViewLayout('grid')} title="Grid">⊞</button>
              <button className={`vl-btn ${viewLayout === 'compact' ? 'active' : ''}`} onClick={() => setViewLayout('compact')} title="Compact">≡</button>
            </div>
            <button className="header-btn" onClick={handleCopyShareLink} title="Copy share link"><Icons.Share size={16} /><span className="btn-label">Share</span></button>
            <button className="header-btn" onClick={handleExport} title="Export to Excel"><Icons.Download size={16} /><span className="btn-label">Export</span></button>
            <div className="header-menu-wrap" ref={menuRef}>
              <button className="header-avatar" onClick={() => setShowMenu(!showMenu)}>
                {userProfile?.firstName?.[0]?.toUpperCase() || 'U'}
              </button>
              {showMenu && (
                <div className="header-dropdown slide-down">
                  <div className="dropdown-header">
                    <span className="dropdown-name">{userProfile?.firstName}</span>
                    <span className="dropdown-email">{currentUser?.email}</span>
                  </div>
                  <div className="dropdown-divider" />
                  <button onClick={() => { setActivePanel('templates'); setShowMenu(false); }}><Icons.FileText size={16} /> Templates & Notes</button>
                  <button onClick={() => { setActivePanel('contacts'); setShowMenu(false); }}><Icons.Users size={16} /> Only Contacts</button>
                  <button onClick={() => { setActivePanel('settings'); setShowMenu(false); }}><Icons.Settings size={16} /> Settings</button>
                  <div className="dropdown-divider" />
                  <button onClick={logout} className="dropdown-danger"><Icons.Logout size={16} /> Sign Out</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <QuickPads />
      <StatsBar stats={stats} />

      <div className="controls-bar">
        <div className="controls-inner">
          <div className="search-box">
            <Icons.Search size={16} />
            <input type="text" value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(1); }} placeholder="Search companies, roles, contacts, or locations..." />
            {searchQuery && <button className="search-clear" onClick={() => { setSearchQuery(''); setPage(1); }}><Icons.X size={14} /></button>}
          </div>
          <div className="controls-right">
            <button className={`filter-toggle ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(!showFilters)}>
              <Icons.Filter size={14} /><span>Filters</span>
              {hasActiveFilters && <span className="filter-badge" />}
            </button>
            <button className={`filter-toggle ${bulkMode ? 'active' : ''}`} onClick={() => { setBulkMode(!bulkMode); setBulkSelected(new Set()); }}>
              <Icons.Trash size={14} /><span>Bulk</span>
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="filters-row slide-down">
            <MultiSelectFilter label="Status" options={STATUS_LIST} selected={statusFilters} onChange={v => { setStatusFilters(v); setPage(1); }} placeholder="All Statuses" />
            <MultiSelectFilter label="Contact Status" options={CONTACT_STATUS_LIST} selected={contactFilters} onChange={v => { setContactFilters(v); setPage(1); }} placeholder="All Contacts" />
            <MultiSelectFilter label="State" options={uniqueStates} selected={stateFilters} onChange={v => { setStateFilters(v); setPage(1); }} placeholder="All States" searchable />
            <MultiSelectFilter label="City" options={uniqueCities} selected={cityFilters} onChange={v => { setCityFilters(v); setPage(1); }} placeholder="All Cities" searchable />
            <MultiSelectFilter
              label="Flag"
              options={['green', 'yellow']}
              selected={flagFilters}
              onChange={v => { setFlagFilters(v); setPage(1); }}
              placeholder="All Flags"
              renderOption={opt => (
                <span style={{display:'flex', alignItems:'center', gap:6}}>
                  <span style={{width:10, height:10, borderRadius:'50%', background: opt === 'green' ? '#16a34a' : '#eab308', display:'inline-block'}} />
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </span>
              )}
            />
            {showDates && (
              <div className="filter-group"><label>Applied After</label>
                <input type="date" value={dateFilter} onChange={e => { setDateFilter(e.target.value); setPage(1); }} />
              </div>
            )}
            <div className="filter-group">
              <label>Update Message</label>
              <button
                className={`update-msg-filter-btn ${updateMsgFilter ? 'active' : ''}`}
                onClick={() => { setUpdateMsgFilter(v => !v); setPage(1); }}
                title="Show only roles with an update message"
              >
                {updateMsgFilter ? '✓ Has Update' : 'Has Update'}
              </button>
            </div>
            {hasActiveFilters && (
              <button className="clear-filters" onClick={() => { setStatusFilters([]); setContactFilters([]); setStateFilters([]); setCityFilters([]); setFlagFilters([]); setDateFilter(''); setUpdateMsgFilter(false); setPage(1); }}>Clear All</button>
            )}
          </div>
        )}

        {bulkMode && !bulkPendingDelete && (
          <div className="bulk-bar slide-down">
            <span className="bulk-count">{bulkSelected.size} selected</span>
            <button className="bulk-select-all" onClick={selectAllFiltered}>Select All ({filteredCompanies.length})</button>
            <button className="bulk-clear" onClick={() => setBulkSelected(new Set())}>Clear</button>
            <button className="bulk-delete-btn" onClick={handleBulkDelete} disabled={bulkSelected.size === 0}><Icons.Trash size={14} /> Delete Selected</button>
          </div>
        )}

        {bulkPendingDelete && (
          <div className="bulk-undo-bar slide-down">
            <span>{bulkPendingDelete.size} compan{bulkPendingDelete.size === 1 ? 'y' : 'ies'} will be deleted in <strong>{bulkCountdown}s</strong></span>
            <button className="cc-undo-btn" onClick={handleBulkUndo}>Undo</button>
          </div>
        )}
      </div>

      <div className="add-company-bar">
        <form onSubmit={handleAddCompany} className="add-company-form">
          <Icons.Building size={16} />
          <input type="text" value={newCompanyName} onChange={e => setNewCompanyName(e.target.value)} placeholder="Add a company..." maxLength={100} />
          <button type="submit" disabled={!newCompanyName.trim()}><Icons.Plus size={16} /><span>Add</span></button>
        </form>
      </div>

      <div className={`company-list company-layout-${viewLayout}`}>
        {paginatedCompanies.length === 0 && !loading && (
          <div className="empty-state">
            {searchQuery || hasActiveFilters ? (
              <><Icons.Search size={32} /><h3>No matches found</h3><p>Try adjusting your search or filters</p></>
            ) : (
              <><Icons.Building size={32} /><h3>No companies yet</h3><p>Add your first company above to start tracking</p></>
            )}
          </div>
        )}

        {paginatedCompanies.map(company => (
          <div key={company.id} className={`company-list-item ${bulkMode ? 'bulk-mode' : ''}`}>
            {bulkMode && (
              <label className="bulk-checkbox"><input type="checkbox" checked={bulkSelected.has(company.id)} onChange={() => toggleBulkSelect(company.id)} /></label>
            )}
            <CompanyCard company={company}
              roles={filteredRoles.filter(r => r.companyId === company.id)}
              contacts={contacts.filter(c => c.companyId === company.id)}
              existingCities={existingCities}
              onUpdateCompany={updateCompany} onDeleteCompany={deleteCompany}
              onAddRole={addRole} onUpdateRole={updateRole} onDeleteRole={deleteRole}
              onAddContact={addContact} onUpdateContact={updateContact} onDeleteContact={deleteContact}
              showDates={showDates} locationMode={locationMode} />
          </div>
        ))}

        {hasMore && <button className="load-more" onClick={() => setPage(p => p + 1)}>Load more ({filteredCompanies.length - paginatedCompanies.length} remaining)</button>}
        <div className="list-footer">Showing {paginatedCompanies.length} of {filteredCompanies.length} companies</div>
      </div>

      {activePanel === 'settings' && <SettingsPanel onClose={() => setActivePanel(null)} />}
      {activePanel === 'templates' && <TemplatesPanel onClose={() => setActivePanel(null)} />}
      {activePanel === 'contacts' && <StandaloneContacts onClose={() => setActivePanel(null)} />}
    </div>
  );
}
